import { isLayeredShape, layeredSelection, isSameColor, getFeltColorHex } from "~/shared/layeredEngraving";
import feltColors from "~/server/utils/threeDDesignSync/felt-colors.json";
import { GRID_PATTERN_IDS } from "~/composables/grid/constants";
import { checkRotationCollision, findNearbyNonCollidingPosition } from "~/components/CanvasGrid/helpers";
import COLOR_CHOICES, { isWoodGrainColor } from "~/composables/shapes/colors";
import { getShapeBaseConfig } from "~/composables/shapes/factory";
import { getColorsForPattern } from "~/composables/shapes/printedPatterns";
import { isRotationLocked, normalizeShapeRotation } from "~/composables/shapes/shapeCapabilities";
import { isBlank3DBox } from "~/composables/shapes/shapesFor3D";
import { getShapeById } from "~/composables/shapes/utils";
import { isWoodSlatFinishLabel, isWoodSlatShapeId } from "~/composables/shapes/woodSlats";
import { Toast } from "~/composables/tools";

export function useShapeInteractions() {
  const store = useGridStore();
  const projectStore = useProjectStore();
  const runtimeConfig = useRuntimeConfig();
  const { selectedColor, selectedItemsOnGrid } = storeToRefs(store);
  const recentColorsStore = useRecentColorsStore();
  const activeShapeGroup = useState<any>("new-studio-active-shape-group", () => null);

  function selectShapesByColor(color: { label: string }) {
    const shapesOnColor = store.shapeList
      .filter((shape: any) => shape.metadata?.color_label === color.label)
      .map((s: any) => s.id);
    store.selectItemsOnGrid(shapesOnColor);
  }

  function changeSelectedShapes(color: any) {
    if (store.isProcessing) return;
    try {
      store.setTempItemByCurrentSelection();
      if (selectedItemsOnGrid.value.length < 1) return;

      const WOOD_GRAIN_LABELS = ['Dark Ash', 'Light Ash', 'Medium Elm', 'Natural Walnut', 'White Oak'];
      const isWoodGrainColor = color && WOOD_GRAIN_LABELS.includes(color.label);

      // Selection controls movement; only color edits expand to the layered set.
      const selectedIds = new Set(selectedItemsOnGrid.value.map(item => item.id));
      const layeredSetIds = new Set(selectedItemsOnGrid.value
        .filter(item => layeredSelection(item))
        .map(item => item.metadata?.set_id || item.attrs?.metadata?.set_id)
        .filter(Boolean));
      const colorTargets = [...selectedItemsOnGrid.value, ...store.shapeList.filter(item => !selectedIds.has(item.id) &&
        layeredSelection(item) && layeredSetIds.has(item.metadata?.set_id || item.attrs?.metadata?.set_id))];
      const preparedChanges = colorTargets.map((item: any) => {
        const shape = getShapeById(item.metadata?.shape_id || item.attrs?.metadata?.shape_id);
        if (!shape) return { item, changedItem: null };

        // Wood grain patterns are only allowed on 3D blank boxes
        if (isWoodGrainColor) {
          const isBlank3D = shape.prefix === 'Cube36x36x4' || shape.prefix === 'Cube36x36x2' ||
            shape.prefix === 'Cube12x12x4' || shape.prefix === 'Cube12x12x2' ||
            shape.prefix === 'Rect12x36x4' || shape.prefix === 'Rect12x36x2';
          if (!isBlank3D) {
            return { item, changedItem: null };
          }
        }

        if (layeredSelection(item)) {
          const savedLayer = layeredSelection(item)!;
          const topColor = store.activeLayer === 'top' ? color : useStudioColors().getColors().find(c => isSameColor(c.label, savedLayer.top_color));
          const hex = color.hex || getFeltColorHex(color.label);
          const layer = store.activeLayer === 'bottom'
            ? { ...savedLayer, bottom_color: color.label, bottom_hex: hex }
            : savedLayer;
          if (!topColor || !layer.bottom_hex) return { item, changedItem: null };
          return { item, changedItem: getShapeBaseConfig(shape, topColor, false, false, { metadata: { ...item.metadata, layered_engraving: layer } }) };
        }
        const allowColorFallback = !isWoodSlatFinishLabel(color?.label) && !isWoodGrainColor;
        return {
          item,
          changedItem: getShapeBaseConfig(shape, color, false, allowColorFallback, item),
        };
      });
      // A finish can be valid for one wood-slat size but not another (Wenge is
      // 8 ft only). Reject the whole recolor before saving or partially
      // changing a mixed selection if any selected module cannot resolve.
      if (preparedChanges.some(change => !change.changedItem)) return;

      store.isProcessing = true;
      store.saveState();

      const changedItemsIDs: string[] = [];
      const promises: Promise<void>[] = [];
      const batch: any[] = [];

      preparedChanges.forEach(({ item, changedItem }) => {
        promises.push(
          new Promise((resolve) => {
            const onReady = () => {
              changedItemsIDs.push(item.id);
              batch.push({
                id: item.id,
                attrs: {
                  fillPatternImage: changedItem!.fillPatternImage,
                  blankFillPatternImage: changedItem!.blankFillPatternImage || null,
                  metadata: changedItem!.metadata,
                },
              });
              resolve();
            };

            if (changedItem!.fillPatternImage.complete && changedItem!.fillPatternImage.naturalWidth > 0) {
              onReady();
            } else {
              changedItem!.fillPatternImage.onload = onReady;
              changedItem!.fillPatternImage.onerror = () => resolve();
            }
          }),
        );
      });

      if (promises.length === 0) {
        store.isProcessing = false;
        return;
      }

      Promise.all(promises).then(() => {
        store.updateBatchOnList(batch);
        store.selectItemsOnGrid(changedItemsIDs.filter(id => selectedIds.has(id)));
        if (runtimeConfig.public.autoSaveOnChange === true) {
          projectStore.save();
        }
        store.isProcessing = false;
      });
    } catch (e) {
      console.error("Error changing shapes color", e);
      store.isProcessing = false;
    }
  }

  function changeShapeRotation(e: Event) {
    if (selectedItemsOnGrid.value.length < 1) return;
    if (selectedItemsOnGrid.value.every(item => isRotationLocked(item))) return;
    const newRotation = Number.parseInt((e.target as HTMLSelectElement).value);

    const snap = store.placementSnap?.x || 30;
    const itemPositions: { item: any; validPos: { x: number; y: number }; rotation: number }[] = [];

    for (const item of selectedItemsOnGrid.value) {
      if (isRotationLocked(item)) continue;
      const shape = getShapeById(item.metadata?.shape_id || item.attrs?.metadata?.shape_id);
      let targetRotation = normalizeShapeRotation(shape, newRotation);
      if (store.gridPattern?.id === GRID_PATTERN_IDS.SQUARE_45 && isLayeredShape(shape) && targetRotation % 90 === 0) {
        targetRotation = (targetRotation + 45) % 360;
      }
      const shapeId = item.metadata?.shape_id || item.attrs?.metadata?.shape_id;
      const validPos = findNearbyNonCollidingPosition(item, shapeId, targetRotation, store.shapeList, snap, 12, store.gridPattern?.id);
      if (!validPos) {
        Toast({
          icon: "warning",
          title: "Cannot rotate tile",
          text: "No space to rotate tile nearby",
          customClass: { popup: "swal-toast-vertical" },
        });
        return;
      }
      itemPositions.push({ item, validPos, rotation: targetRotation });
    }

    store.saveState();
    store.setSelectedRotation(newRotation);

    const changedItemsIDs: string[] = [];
    itemPositions.forEach(({ item, validPos, rotation }) => {
      item.x = validPos.x;
      item.y = validPos.y;
      item.rotation = rotation;
      changedItemsIDs.push(item.id);
      store.updateItemOnList(item.id, { x: validPos.x, y: validPos.y, attrs: { rotation } });
    });

    store.selectItemsOnGrid(changedItemsIDs);
    if (runtimeConfig.public.autoSaveOnChange === true) {
      projectStore.save();
    }
  }

  async function changeSelectedShape(shape: any) {
    if (store.isProcessing) return;
    if (isLayeredShape(shape)) {
      if (!selectedItemsOnGrid.value.length || selectedItemsOnGrid.value.some(item => !layeredSelection(item))) return;
      const selectedIds = selectedItemsOnGrid.value.map(item => item.id);
      const setIds = new Set(selectedItemsOnGrid.value.map(item => (item.metadata || item.attrs?.metadata)?.set_id).filter(Boolean));
      const targets = store.shapeList.filter(item => layeredSelection(item) && setIds.has((item.metadata || item.attrs?.metadata)?.set_id));
      if (!targets.length) return;
      const changes = targets.map(item => {
        const metadata = item.metadata || item.attrs?.metadata;
        const color = useStudioColors().getColors().find(c => isSameColor(c.label, layeredSelection(item)!.top_color));
        const next = color && getShapeBaseConfig(shape, color, false, false, item);
        return { item, metadata, next };
      });
      if (changes.some(change => !change.next)) return;
      store.isProcessing = true;
      try {
        await Promise.all(changes.map(({ next }) => new Promise<void>((resolve, reject) => {
          const image = next!.fillPatternImage;
          if (image.complete && image.naturalWidth > 0) return resolve();
          image.onload = () => resolve();
          image.onerror = () => reject(new Error('Unable to load layered engraving preview'));
        })));
        store.saveState();
        store.updateBatchOnList(changes.map(({ item, metadata, next }) => ({
          id: item.id,
          attrs: { fillPatternImage: next!.fillPatternImage, metadata: { ...metadata, ...next!.metadata } },
        })));
        store.selectItemsOnGrid(selectedIds);
        if (runtimeConfig.public.autoSaveOnChange === true) projectStore.save();
      } catch (error) {
        console.error('Error changing layered engraving', error);
      } finally {
        store.isProcessing = false;
      }
      return;
    }
    if (selectedItemsOnGrid.value.some(item => layeredSelection(item))) return;
    if (selectedItemsOnGrid.value.length < 1 || !selectedColor.value) return;
    // Engraving changes must preserve the active color. Do not let the
    // variation lookup silently fall back to another color's product variant.
    const nextShape = getShapeBaseConfig(shape, selectedColor.value, false, false);
    if (!nextShape) return;

    store.isProcessing = true;
    store.saveState();
    nextShape.fillPatternImage.onload = () => {
      const selectedIds = selectedItemsOnGrid.value.map(item => item.id);
      // Preserve the existing rotation of selected items (e.g. 45° on the 45-deg grid)
      const existingRotation = selectedItemsOnGrid.value[0]?.rotation
        ?? selectedItemsOnGrid.value[0]?.attrs?.rotation;
      const rotation = normalizeShapeRotation(shape, existingRotation);
      store.updateBatchOnList(selectedIds.map(id => ({
        id,
        attrs: {
          fillPatternImage: nextShape.fillPatternImage,
          metadata: nextShape.metadata,
          rotation,
        },
      })));
      store.selectItemsOnGrid(selectedIds);
      if (runtimeConfig.public.autoSaveOnChange === true) projectStore.save();
      store.isProcessing = false;
    };
  }

  function handleColorClick(color: any, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    // Clear stale temp preview — if the new color doesn't resolve to a variation
    // (e.g. Printed 2x2 pattern + any color pre-seed), the watcher's early return
    // would otherwise leave a stale preview committable on canvas click.
    store.setTempNewItems([]);
    if (store.isProcessing) return;
    if (isLayeredShape(store.selectedShape) && store.activeLayer === 'bottom') {
      const hex = color.hex || getFeltColorHex(color.label);
      if (!hex) return;
      store.layeredBottomColor = { ...color, hex };
    } else {
      const hex = color.hex || getFeltColorHex(color.label);
      selectedColor.value = hex ? { ...color, hex } : color;
    }

    const target = event.target as HTMLElement;
    if (target) {
      target.blur();
      const label =
        target.closest("label") || (target.tagName === "LABEL" ? target : null);
      if (label) (label as HTMLElement).blur();
    }

    setTimeout(() => {
      if (projectStore.gridRef) {
        const stage =
          (projectStore.gridRef as any).getStage?.() || projectStore.gridRef;
        const stageElement = stage.container?.() || stage;
        if (stageElement?.focus) stageElement.focus({ preventScroll: true });
      }
    }, 0);

    // Record the committed color for the mobile drawer's "Recently Used"
    // row. Hydrate lazily on first use so SSR doesn't try to touch
    // localStorage; the actual write happens only on the client.
    recentColorsStore.hydrate();
    if (color?.label) recentColorsStore.addRecentColor(color.label);

    changeSelectedShapes(color);
    try {
      if (store.selectedShape) {
        store.setTempItemByCurrentSelection();
      }
    } catch (e) {
      // non-blocking
    }
  }

  function ensureColorIsAllowedFor(shape: any) {
    if (!selectedColor.value) return;

    // Wood slat finishes are only allowed on wood slats.
    if (isWoodSlatFinishLabel(selectedColor.value.label) && !isWoodSlatShapeId(shape?.id)) {
      const firstStandardColor = useStudioColors()
        .getColors()
        .find((color: any) => !isWoodSlatFinishLabel(color.label) && !isWoodGrainColor(color.label) && color.active) || COLOR_CHOICES[0];
      if (firstStandardColor) selectedColor.value = firstStandardColor;
      return;
    }

    // Wood grain patterns are ONLY allowed on blank 3D boxes.
    // If the currently-selected color is a wood grain pattern but the target
    // shape is NOT a blank 3D box (e.g. 3D engraved box or standard tile),
    // switch to a regular felt color so 3D engraved boxes don't get wood grain sides.
    if (isWoodGrainColor(selectedColor.value.label) && !isBlank3DBox(shape?.prefix)) {
      const regularColor = useStudioColors()
        .getColors()
        .find((c: any) => !isWoodGrainColor(c.label) && !isWoodSlatFinishLabel(c.label) && c.active) || COLOR_CHOICES[0];
      if (regularColor) selectedColor.value = regularColor;
      return;
    }

    // If the shape restricts allowed colors (e.g. Printed 2x2: Aspen → 4 colors, or preconfigured finish),
    // and the currently-selected color isn't in the allowlist, auto-switch to
    // the first allowed color so the user can place the shape immediately.
const allowlist = shape?.allowedColors || getColorsForPattern(shape?.prefix, useStudioColors().getColors());
    if (allowlist && !allowlist.includes(selectedColor.value.label)) {
      const firstAllowed = useStudioColors()
        .getColors()
        .find((c: any) => c.label === allowlist[0]);
      if (firstAllowed) selectedColor.value = firstAllowed;
    }
  }

  function handleShapeClick(shape: any) {
    if (isLayeredShape(shape) || isLayeredShape(store.selectedShape)) store.unselectItemsOnGrid();
    store.setSelectedRotation(null);
    store.setTempNewItems([]);
    ensureColorIsAllowedFor(shape);
    store.setSelectedShape(shape);
    try {
      store.setTempItemByCurrentSelection();
    } catch (e) {
      // non-blocking
    }
  }

  function handleParentShapeClick(shape: any) {
    if (isLayeredShape(shape) || isLayeredShape(store.selectedShape)) store.unselectItemsOnGrid();
    store.setSelectedRotation(null);
    store.setTempNewItems([]);
    if (!shape.children?.some((c: any) => c.id === store.selectedShape?.id)) {
      ensureColorIsAllowedFor(shape);
      store.setSelectedShape(shape);
      try {
        store.setTempItemByCurrentSelection();
      } catch (e) {
        // non-blocking
      }
    }
    activeShapeGroup.value =
      activeShapeGroup.value?.id === shape.id ? null : shape;
  }

  function closeShapeGroup() {
    activeShapeGroup.value = null;
  }

  return {
    activeShapeGroup,
    selectShapesByColor,
    changeSelectedShapes,
    changeSelectedShape,
    changeShapeRotation,
    handleColorClick,
    handleShapeClick,
    handleParentShapeClick,
    closeShapeGroup,
  };
}
